import { supabase } from './supabase';

export async function loadVendors(orgId) {
  if (!orgId) return [];
  const { data, error } = await supabase
    .from('vendors')
    .select('id, name, email_domain, contact_name, contact_email, contact_phone, notes, is_active, vendor_category')
    .eq('org_id', orgId)
    .eq('is_active', true)
    .eq('vendor_category', 'inventory')
    .order('name', { ascending: true });
  if (error) { console.error('loadVendors error:', error); return []; }
  return data || [];
}

export async function loadVendorNames(orgId) {
  const vendors = await loadVendors(orgId);
  return vendors.map(v => v.name);
}

export async function loadPayees(orgId) {
  if (!orgId) return [];
  const { data, error } = await supabase
    .from('vendors')
    .select('id, name, email_domain, contact_name, contact_email, contact_phone, notes, is_active, vendor_category, default_expense_category')
    .eq('org_id', orgId)
    .eq('vendor_category', 'expense')
    .order('name', { ascending: true });
  if (error) { console.error('loadPayees error:', error); return []; }
  return data || [];
}

export async function loadAllVendorsForUI(orgId) {
  if (!orgId) return [];
  const { data, error } = await supabase
    .from('vendors')
    .select('id, name, email_domain, contact_name, contact_email, contact_phone, notes, is_active, monitoring_mode, vendor_category, default_expense_category')
    .eq('org_id', orgId)
    .order('name', { ascending: true });
  if (error) { console.error('loadAllVendorsForUI error:', error); return []; }
  return data || [];
}

export async function createVendor(orgId, { name, email_domain, contact_name, contact_email, contact_phone, notes, vendor_category, default_expense_category }) {
  const trimmed = (name || '').trim();
  if (!trimmed) throw new Error('Vendor name is required');
  const cat = vendor_category || 'inventory';
  const row = {
    org_id: orgId,
    name: trimmed,
    email_domain: email_domain || null,
    contact_name: contact_name || null,
    contact_email: contact_email || null,
    contact_phone: contact_phone || null,
    notes: notes || null,
    vendor_category: cat,
    default_expense_category: cat === 'expense' ? (default_expense_category || null) : null
  };
  const { data, error } = await supabase
    .from('vendors')
    .insert(row)
    .select()
    .single();
  if (error) {
    if (error.code === '23505') throw new Error('A vendor with that name already exists.');
    throw error;
  }
  return data;
}

export async function updateVendor(vendorId, fields) {
  const payload = {};
  if ('name' in fields) {
    const trimmed = (fields.name || '').trim();
    if (!trimmed) throw new Error('Vendor name is required');
    payload.name = trimmed;
  }
  if ('email_domain' in fields) payload.email_domain = fields.email_domain || null;
  if ('contact_name' in fields) payload.contact_name = fields.contact_name || null;
  if ('contact_email' in fields) payload.contact_email = fields.contact_email || null;
  if ('contact_phone' in fields) payload.contact_phone = fields.contact_phone || null;
  if ('notes' in fields) payload.notes = fields.notes || null;
  if ('is_active' in fields) payload.is_active = fields.is_active;
  if ('vendor_category' in fields) {
    payload.vendor_category = fields.vendor_category;
  }
  if ('default_expense_category' in fields) payload.default_expense_category = fields.default_expense_category || null;
  const { data, error } = await supabase
    .from('vendors')
    .update(payload)
    .eq('id', vendorId)
    .select()
    .single();
  if (error) {
    if (error.code === '23505') throw new Error('A vendor with that name already exists.');
    throw error;
  }
  return data;
}

export async function getVendorCategory(orgId, vendorName) {
  if (!orgId || !vendorName) return null;
  // Try exact name match first
  const { data } = await supabase
    .from('vendors')
    .select('vendor_category')
    .eq('org_id', orgId)
    .ilike('name', vendorName.trim())
    .limit(1)
    .single();
  if (data?.vendor_category) return data.vendor_category;
  // Try alias match
  const { data: aliasRow } = await supabase
    .from('vendor_aliases')
    .select('vendor_id')
    .eq('org_id', orgId)
    .ilike('alias', vendorName.trim())
    .limit(1)
    .single();
  if (!aliasRow) return null;
  const { data: v } = await supabase
    .from('vendors')
    .select('vendor_category')
    .eq('id', aliasRow.vendor_id)
    .single();
  return v?.vendor_category || null;
}

export async function resolveVendor(orgId, vendorName) {
  if (!orgId || !vendorName) return null;
  const trimmed = vendorName.trim();
  // Try exact name match
  const { data: byName } = await supabase
    .from('vendors')
    .select('id, name, vendor_category, default_expense_category')
    .eq('org_id', orgId)
    .ilike('name', trimmed)
    .limit(1)
    .single();
  if (byName) return byName;
  // Try alias match
  const { data: aliasRow } = await supabase
    .from('vendor_aliases')
    .select('vendor_id')
    .eq('org_id', orgId)
    .ilike('alias', trimmed)
    .limit(1)
    .single();
  if (!aliasRow) return null;
  const { data: byAlias } = await supabase
    .from('vendors')
    .select('id, name, vendor_category, default_expense_category')
    .eq('id', aliasRow.vendor_id)
    .single();
  return byAlias || null;
}

const STRIP_WORDS = /\b(llc|inc|corp|co|dba|ltd|limited|company|enterprises?|group|holdings?)\b/gi;
function normalizeForMatch(s) {
  return (s || '').toLowerCase().replace(STRIP_WORDS, '').replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
}

export async function findSimilarVendors(orgId, vendorName) {
  if (!orgId || !vendorName) return [];
  const { data: all } = await supabase
    .from('vendors')
    .select('id, name, vendor_category, default_expense_category')
    .eq('org_id', orgId)
    .eq('is_active', true)
    .order('name');
  if (!all?.length) return [];
  const normInput = normalizeForMatch(vendorName);
  const inputTokens = normInput.split(/\s+/).filter(Boolean);
  if (!inputTokens.length) return [];
  const scored = [];
  for (const v of all) {
    const normV = normalizeForMatch(v.name);
    const vTokens = normV.split(/\s+/).filter(Boolean);
    if (!vTokens.length) continue;
    const shared = inputTokens.filter(t => vTokens.includes(t)).length;
    let score = shared / Math.max(inputTokens.length, vTokens.length);
    // Containment boost
    if (normInput.includes(normV) || normV.includes(normInput)) score += 0.3;
    if (score >= 0.5) scored.push({ ...v, score: Math.min(score, 1.0) });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 3);
}

export async function assignVendorAlias(orgId, vendorId, alias) {
  const trimmed = (alias || '').trim();
  const letters = (trimmed.match(/[a-zA-Z]/g) || []).length;
  if (trimmed.length < 3 || trimmed.length > 100 || letters === 0 || letters / trimmed.length < 0.3) {
    console.warn('[vendors] Rejected vendor alias — looks like garbage:', JSON.stringify(trimmed));
    return;
  }
  const { error } = await supabase
    .from('vendor_aliases')
    .insert({ org_id: orgId, vendor_id: vendorId, alias: trimmed });
  if (error && error.code !== '23505') throw error;
}

export async function deleteVendor(vendorId) {
  const { error } = await supabase
    .from('vendors')
    .delete()
    .eq('id', vendorId);
  if (error) throw error;
  return { success: true };
}
